import { useEffect, useState, useMemo, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L, { DivIcon } from 'leaflet';

interface AncientRoadsLayerProps {
    show: boolean;
    showSeaLanes: boolean;
    selectedYear: number;
}

// Minimum zoom level to show road labels (prevents clutter when zoomed out)
const MIN_ZOOM_FOR_LABELS = 10;

// Memoized style function - created once and reused
const getRoadStyle = (feature: any) => {
    const roadType = feature?.properties?.type || 'Secondary Road';
    const certainty = feature?.properties?.segmentCertainty?.toLowerCase() || 'certain';
    
    // Ancient map colors - earthy tones (Tan/beige for other types)
    let color = '#BC9A7C';
    let weight = 2;
    let opacity = 0.5;
    
    if (roadType === 'Main Road') {
        color = '#8B4513';    // Saddle brown - prominent but natural
        weight = 5;           // Thick for main roads
        opacity = 0.85;       // Strong presence
    } else if (roadType === 'Secondary Road') {
        color = '#A0826D';    // Lighter brown
        weight = 2.5;         // Medium for secondary roads
        opacity = 0.65;       // More subtle
    } else if (roadType === 'River') {
        color = '#4682B4';    // Steel blue for rivers
        weight = 3;
        opacity = 0.7;
    } else if (roadType === 'Sea Lane') {
        color = '#5F9EA0';    // Cadet blue for sea lanes
        weight = 2;
        opacity = 0.6;
    }
    
    // Adjust opacity for conjectured/hypothetical roads
    if (certainty === 'conjectured') {
        opacity *= 0.8;
    } else if (certainty === 'hypothetical') {
        opacity *= 0.6;
    }
    
    return {
        color,
        weight,
        opacity,
        dashArray: certainty === 'hypothetical' ? '8, 4' : undefined,
        className: 'cursor-pointer',
        interactive: true
    };
};

// Helper to format year for popup
function formatYear(year: number | null | undefined): string {
    if (year === null || year === undefined || year === 0) return 'Unknown';
    return year < 0 ? `${Math.abs(year)} BC` : `${year} AD`;
}

/**
 * Component to display ancient roads from the Itiner-e dataset
 * Data source: https://itiner-e.org/
 * Includes Roman, Hellenistic, Persian, and other ancient Mediterranean routes
 * Filters roads by the selected timeline year
 */
/**
 * Create a text label for a major road
 */
function createRoadLabel(name: string): DivIcon {
    const displayName = name.toUpperCase();
    const fontSize = 11;
    const fontWeight = 600;
    const color = '#704214'; // Dark brown for major routes
    const textShadow = '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.8)';
    
    return new DivIcon({
        html: `<div style="
            font-size: ${fontSize}px; 
            font-weight: ${fontWeight}; 
            color: ${color}; 
            text-shadow: ${textShadow};
            white-space: nowrap;
            pointer-events: none;
            letter-spacing: 1px;
        ">${displayName}</div>`,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [0, 0]
    });
}

/**
 * Calculate the center point of a LineString geometry
 */
function getLineStringCenter(coordinates: number[][]): [number, number] | null {
    if (!coordinates || coordinates.length === 0) return null;
    
    // Use the middle point of the line
    const midIndex = Math.floor(coordinates.length / 2);
    const [lng, lat] = coordinates[midIndex];
    return [lat, lng]; // Leaflet uses [lat, lng]
}

interface RoadLabel {
    marker: L.Marker;
    roadName: string;
}

export function AncientRoadsLayer({ show, showSeaLanes, selectedYear }: AncientRoadsLayerProps) {
    const map = useMap();
    const [roadsLayer, setRoadsLayer] = useState<L.GeoJSON | null>(null);
    const [roadLabels, setRoadLabels] = useState<RoadLabel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [allFeatures, setAllFeatures] = useState<unknown[]>([]);
    const [currentZoom, setCurrentZoom] = useState(map.getZoom());

    // Lazy-load popup content generator
    const generatePopupContent = useCallback((feature: any) => {
        const { 
            name, 
            type, 
            constructionPeriod, 
            segmentCertainty,
            lowerDate,
            upperDate,
            itinerary
        } = feature.properties;
        
        const isUndated = lowerDate === null || upperDate === null || 
                        lowerDate === undefined || upperDate === undefined ||
                        (lowerDate === 0 && upperDate === 0);
        
        const dateRange = isUndated
            ? 'Unknown' 
            : `${formatYear(lowerDate)} - ${formatYear(upperDate)}`;
        
        // Get connection points from places (optimized data)
        const places = feature.places || [];
        const placesHtml = places.length > 0 ? `
            <div class="border-t border-gray-200 pt-2 mt-2">
                <p class="font-semibold text-xs mb-1">Connection Points:</p>
                <div class="space-y-1 max-h-32 overflow-y-auto">
                    ${places.slice(0, 8).map((p: any) => `
                        <div class="text-xs">
                            <span class="font-medium">${p.name}</span>
                            <span class="text-gray-600"> (${p.type})</span>
                        </div>
                    `).join('')}
                    ${places.length > 8 ? `<p class="text-xs text-gray-500 italic">+${places.length - 8} more</p>` : ''}
                </div>
            </div>
        ` : '';
        
        return `
            <div class="space-y-1 text-sm">
                <h3 class="font-semibold text-base">${name || 'Ancient Road'}</h3>
                ${type ? `<p><strong>Type:</strong> ${type}</p>` : ''}
                <p><strong>Date Range:</strong> ${dateRange}</p>
                ${constructionPeriod ? `<p><strong>Period:</strong> ${constructionPeriod}</p>` : ''}
                ${segmentCertainty ? `<p><strong>Certainty:</strong> ${segmentCertainty}</p>` : ''}
                ${itinerary ? `<p><strong>Source:</strong> ${itinerary}</p>` : ''}
                ${placesHtml}
            </div>
        `;
    }, []);

    // Effect 0: Track zoom level changes
    useEffect(() => {
        const handleZoomEnd = () => {
            setCurrentZoom(map.getZoom());
        };
        
        map.on('zoomend', handleZoomEnd);
        
        return () => {
            map.off('zoomend', handleZoomEnd);
        };
    }, [map]);

    // Effect 1: Load the data only when show or showSeaLanes becomes true
    useEffect(() => {
        // Only load if either show or showSeaLanes is true and we haven't loaded yet
        if ((!show && !showSeaLanes) || isLoaded || isLoading) {
            return;
        }

        setIsLoading(true);

        // Fetch and parse the optimized NDJSON file
        fetch('/ancient-roads-optimized.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then(text => {
                // Parse NDJSON (each line is a separate JSON object)
                const lines = text.trim().split('\n');
                const features = lines.map(line => JSON.parse(line));

                setAllFeatures(features);
                setIsLoaded(true);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('❌ Error loading ancient roads:', error);
                setIsLoading(false);
            });
    }, [show, showSeaLanes, isLoaded, isLoading]);

    // Effect 2: Create layer once when data is loaded
    useEffect(() => {
        // Wait for data to load
        if (allFeatures.length === 0) {
            return;
        }

        // If layer already exists, don't recreate it
        if (roadsLayer) {
            // Just ensure it's added to the map if either show or showSeaLanes is true
            if ((show || showSeaLanes) && !map.hasLayer(roadsLayer)) {
                roadsLayer.addTo(map);
            }
            return;
        }

        const geojsonData = {
            type: 'FeatureCollection',
            features: allFeatures
        };

        // Create a custom pane for roads to control z-index
        if (!map.getPane('roadsPane')) {
            const pane = map.createPane('roadsPane');
            pane.style.zIndex = '400'; // Below markers (600) but above tiles (200)
        }

                // Create a GeoJSON layer with all roads using memoized style function
        const layer = L.geoJSON(geojsonData as GeoJSON.FeatureCollection, {
            pane: 'roadsPane',
            style: getRoadStyle,
            onEachFeature: (feature, layer) => {
                // Add interaction effects for both desktop (hover) and mobile (tap)
                layer.on({
                    mouseover: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: l.options.weight + 2,
                            opacity: Math.min(l.options.opacity + 0.2, 1.0)
                        });
                    },
                    mouseout: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: l.options.weight - 2,
                            opacity: l.options.opacity - 0.2
                        });
                    },
                    // Mobile tap feedback
                    click: (e) => {
                        const l = e.target;
                        // Brief highlight on tap
                        l.setStyle({
                            weight: l.options.weight + 3,
                            opacity: 1.0
                        });
                        setTimeout(() => {
                            l.setStyle({
                                weight: l.options.weight,
                                opacity: l.options.opacity
                            });
                        }, 200);
                    }
                });
                
                // Lazy-load popup content - only generate when popup is opened
                if (feature.properties) {
                    const popup = L.popup({ maxWidth: 300 });
                    layer.bindPopup(popup);
                    
                    // Generate content only when popup opens
                    layer.on('popupopen', () => {
                        const content = generatePopupContent(feature);
                        popup.setContent(content);
                    });
                }
            }
        });

        // Add layer to map, but immediately filter based on show/showSeaLanes
        layer.addTo(map);
        
        // Create labels for Main Roads - deduplicate by name to avoid multiple labels for segmented roads
        const labels: RoadLabel[] = [];
        const labeledRoadNames = new Set<string>();
        
        // Sort by length descending to label the longest segment of each road
        const sortedFeatures = [...allFeatures].sort((a: any, b: any) => {
            const aLen = a.properties?._lengthInKm || 0;
            const bLen = b.properties?._lengthInKm || 0;
            return bLen - aLen;
        });
        
        sortedFeatures.forEach((feature: any) => {
            const { name, type } = feature.properties || {};
            const geometry = feature.geometry;
            
            // Only label Main Roads, deduplicate by name (one label per road)
            // Sort ensures we label the longest segment of each road
            if (type === 'Main Road' && name && geometry?.type === 'LineString' && 
                !labeledRoadNames.has(name)) {
                const center = getLineStringCenter(geometry.coordinates);
                if (center) {
                    const marker = L.marker(center, {
                        icon: createRoadLabel(name),
                        interactive: false,
                        pane: 'roadsPane'
                    });
                    labels.push({ marker, roadName: name });
                    labeledRoadNames.add(name);
                }
            }
        });
        
        setRoadsLayer(layer);
        setRoadLabels(labels);
    }, [map, show, showSeaLanes, allFeatures, roadsLayer]);

    // Effect 3: Update visibility when selectedYear, show, or showSeaLanes changes
    // Uses opacity for better performance instead of add/remove
    useEffect(() => {
        if (!roadsLayer) {
            return;
        }

        // Track which main roads are visible for label management
        const visibleMainRoadNames = new Set<string>();

        // Efficiently toggle visibility using opacity instead of add/remove
        roadsLayer.eachLayer((layer: any) => {
            const feature = layer.feature;
            if (feature && feature.properties) {
                const roadType = feature.properties.type;
                const roadName = feature.properties.name;
                const lowerDate = feature.properties.lowerDate;
                const upperDate = feature.properties.upperDate;
                
                // Check if this is a sea lane
                const isSeaLane = roadType === 'Sea Lane';
                
                // Determine if this type of road should be shown based on checkboxes
                const typeAllowed = (isSeaLane && showSeaLanes) || (!isSeaLane && show);
                
                if (!typeAllowed) {
                    layer.setStyle({ opacity: 0, fillOpacity: 0 });
                    return;
                }
                
                // Determine if road should be visible based on date
                // Show if undated (0, null, or undefined) or if within date range
                const isUndated = (lowerDate === 0 && upperDate === 0) || 
                                  lowerDate === null || upperDate === null ||
                                  lowerDate === undefined || upperDate === undefined;
                const shouldShow = isUndated || 
                                   (selectedYear >= lowerDate && selectedYear <= upperDate);
                
                // Toggle visibility using opacity
                if (shouldShow) {
                    // Restore original opacity from style
                    const style = getRoadStyle(feature);
                    layer.setStyle({ opacity: style.opacity, fillOpacity: style.opacity });
                    
                    // Track visible main roads for labels (only if roads are enabled)
                    if (show && roadType === 'Main Road' && roadName) {
                        visibleMainRoadNames.add(roadName);
                    }
                } else {
                    layer.setStyle({ opacity: 0, fillOpacity: 0 });
                }
            }
        });

        // Manage road labels - show/hide based on specific road visibility AND zoom level
        const isZoomedIn = currentZoom >= MIN_ZOOM_FOR_LABELS;
        
        roadLabels.forEach(({ marker, roadName }) => {
            const shouldShowLabel = show && isZoomedIn && visibleMainRoadNames.has(roadName);
            
            if (shouldShowLabel) {
                if (!map.hasLayer(marker)) {
                    marker.addTo(map);
                }
            } else {
                if (map.hasLayer(marker)) {
                    map.removeLayer(marker);
                }
            }
        });
    }, [selectedYear, roadsLayer, roadLabels, show, showSeaLanes, map, currentZoom]);

    return null;
}
